'use client'

import Script from 'next/script'
import { useEffect } from 'react'

// ============================================
// CONFIGURACIÓN DE TRACKING - AIRLOVERS
// ============================================

const GA4_ID = process.env.NEXT_PUBLIC_GA4_ID || 'G-0QCZ2VZVC4'
const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID || ''
const FACEBOOK_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID || '2171750063670210'

export default function Tracking() {
  // ============================================
  // EVENTOS DE CONVERSIÓN - trackeo automático
  // ============================================
  useEffect(() => {
    // Función helper para enviar eventos
    const trackEvent = (eventName: string, params: any = {}) => {
      // Google Analytics 4
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', eventName, params)
      }
      // Google Ads
      if (typeof window !== 'undefined' && (window as any).gtag && GOOGLE_ADS_ID) {
        (window as any).gtag('event', 'conversion', {
          send_to: GOOGLE_ADS_ID,
          event_name: eventName,
          ...params
        })
      }
      // Facebook Pixel
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('trackCustom', eventName, params)
      }
      console.log('[Tracking]', eventName, params)
    }

    // Exponer función global para que otros componentes la usen
    ;(window as any).airloversTrack = trackEvent

    // 1. Trackear TODOS los clicks en links de WhatsApp
    const handleWhatsAppClicks = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const link = target.closest('a[href*="wa.me"], a[href*="whatsapp"]') as HTMLAnchorElement
      if (link) {
        const href = link.href
        // Detectar tipo de paquete según el texto del enlace o la URL
        let packageType = 'general'
        const text = link.textContent?.toLowerCase() || ''
        const hrefLower = href.toLowerCase()

        if (text.includes('cumple') || hrefLower.includes('cumple')) packageType = 'paquete_cumpleanos'
        else if (text.includes('aniversario') || hrefLower.includes('aniversario')) packageType = 'paquete_aniversario'
        else if (text.includes('propuesta') || hrefLower.includes('propuesta')) packageType = 'paquete_propuesta'
        else if (text.includes('grupo') || hrefLower.includes('grupo')) packageType = 'paquete_grupal'
        else if (text.includes('pro') || hrefLower.includes('pro')) packageType = 'vuelo_pro'
        else if (text.includes('classic') || hrefLower.includes('classic') || text.includes('clásico')) packageType = 'vuelo_clasico'

        trackEvent('whatsapp_click', {
          package_type: packageType,
          link_text: link.textContent?.trim().substring(0, 50) || 'sin_texto',
          link_url: href,
          page_path: window.location.pathname,
          page_title: document.title
        })
      }
    }

    // 2. Trackear scroll深度 (25%, 50%, 75%, 100%)
    let scrollMarks = new Set<number>()
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      )
      ;[25, 50, 75, 100].forEach(mark => {
        if (scrollPercent >= mark && !scrollMarks.has(mark)) {
          scrollMarks.add(mark)
          trackEvent('scroll_depth', { percent: mark, page_path: window.location.pathname })
        }
      })
    }

    // 3. Trackear tiempo en página (30s, 60s, 120s, 300s)
    const timeMarks = [30, 60, 120, 300]
    const timeouts = timeMarks.map(sec =>
      setTimeout(() => {
        trackEvent('time_on_page', { seconds: sec, page_path: window.location.pathname })
      }, sec * 1000)
    )

    // 4. Trackear vistas de páginas importantes
    const importantPages = ['/cumpleanos', '/aniversarios', '/propuesta', '/vuelos-grupales', '/paquetes-combinados', '/opiniones']
    if (importantPages.includes(window.location.pathname)) {
      trackEvent('view_important_page', {
        page_path: window.location.pathname,
        page_category: window.location.pathname.replace('/', '')
      })
    }

    // Registrar listeners
    document.addEventListener('click', handleWhatsAppClicks)
    window.addEventListener('scroll', handleScroll)

    // Cleanup
    return () => {
      document.removeEventListener('click', handleWhatsAppClicks)
      window.removeEventListener('scroll', handleScroll)
      timeouts.forEach(t => clearTimeout(t))
    }
  }, [])

  return (
    <>
      {/* ============================================
          GOOGLE ANALYTICS 4 (GA4) - SIEMPRE ACTIVO
          ============================================ */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`}
        strategy="afterInteractive"
      />
      <Script
        id="google-analytics-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GA4_ID}', {
              page_path: window.location.pathname,
              send_page_view: true,
            });
          `,
        }}
      />

      {/* ============================================
          GOOGLE ADS CONVERSION TRACKING
          ============================================ */}
      {GOOGLE_ADS_ID && GOOGLE_ADS_ID !== 'AW-XXXXXXXXX' && (
        <Script
          id="google-ads-init"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              gtag('config', '${GOOGLE_ADS_ID}', {
                send_page_view: true
              });
            `,
          }}
        />
      )}

      {/* ============================================
          FACEBOOK PIXEL (META PIXEL) - SIEMPRE ACTIVO
          ============================================ */}
      <Script
        id="facebook-pixel-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${FACEBOOK_PIXEL_ID}');
            fbq('track', 'PageView');
          `,
        }}
      />
    </>
  )
}
